import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { graphql } from 'react-relay';
import { useRelayEnvironment } from 'react-relay/hooks';
import { fetchQuery, type Subscription } from 'relay-runtime';

import type { useHydratedUsersActionQuery } from './__generated__/useHydratedUsersActionQuery.graphql';

export type HydratedUser = { accountId: string; name: string | null };

export type HydratedUsersResult = {
	users: HydratedUser[];
	isLoading: boolean;
	error: Error | null;
};

function normalizeAccountIds(ids: string[] | null | undefined): string[] {
	return Array.from(
		new Set(
			(ids ?? [])
				.map(String)
				.map((s) => s.trim())
				.filter(Boolean),
		),
	).sort();
}

// Check if the accountIds are already hydrated
function isAlreadyHydrated(users: HydratedUser[], accountIds: string[]) {
	return (
		users.length > 0 &&
		accountIds.every((accountId) => users.some((user) => user.accountId === accountId && user.name))
	);
}

export function useHydratedUsers(
	suggestedAccountIds: string[] | null | undefined,
	shouldHydrate: boolean,
): HydratedUsersResult | null {
	const environment = useRelayEnvironment();

	const [users, setUsers] = useState<HydratedUser[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);

	const accountIds = useMemo(() => normalizeAccountIds(suggestedAccountIds), [suggestedAccountIds]);

	// accountIdsKey is used as a stable dependency for useEffect and request tracking.
	// This avoids infinite update loops caused by new array references on every render.
	const accountIdsKey = accountIds.join(',');

	const inFlightFor = useRef<string | null>(null);
	const activeSubscription = useRef<Subscription | null>(null);
	const disposed = useRef<boolean>(false);

	useEffect(() => {
		disposed.current = false;
		return () => {
			disposed.current = true;
			activeSubscription.current?.unsubscribe();
			activeSubscription.current = null;
			inFlightFor.current = null;
		};
	}, []);

	const resetState = useCallback(() => {
		if (inFlightFor.current) {
			activeSubscription.current?.unsubscribe();
			activeSubscription.current = null;
			inFlightFor.current = null;
		}
		setUsers([]);
		setIsLoading(false);
		setError(null);
	}, []);

	// Cancel any previous in-flight request
	const cancelPreviousRequest = useCallback(() => {
		if (activeSubscription.current) {
			activeSubscription.current.unsubscribe();
			activeSubscription.current = null;
		}
	}, []);

	// Cancel the subscription
	const cancelSubscription = useCallback((subscription: Subscription, key: string) => {
		if (activeSubscription.current === subscription) {
			subscription.unsubscribe();
			activeSubscription.current = null;
			if (inFlightFor.current === key) {
				inFlightFor.current = null;
			}
		}
	}, []);

	useEffect(() => {
		if (accountIds.length === 0 || !shouldHydrate) {
			resetState();
			return;
		}

		if (isAlreadyHydrated(users, accountIds)) {
			return; // already hydrated
		}

		if (inFlightFor.current === accountIdsKey) {
			return; // request already in flight for this set
		}

		// cancel any previous in-flight subscription
		cancelPreviousRequest();

		inFlightFor.current = accountIdsKey;
		setIsLoading(true);
		setError(null);

		const subscription = fetchQuery<useHydratedUsersActionQuery>(
			environment,
			graphql`
				query useHydratedUsersActionQuery($accountIds: [ID!]!) {
					users(accountIds: $accountIds) {
						accountId
						name
					}
				}
			`,
			{ accountIds: accountIds },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (response) => {
				if (disposed.current) {
					return;
				}

				if (inFlightFor.current !== accountIdsKey) {
					return;
				}

				const hydratedUsers = (response?.users ?? []).filter(Boolean).map((user) => ({
					accountId: user?.accountId,
					name: user?.name ?? null,
				}));

				setUsers(hydratedUsers);
				setIsLoading(false);
			},
			error: (error: Error) => {
				if (disposed.current) {
					return;
				}
				if (inFlightFor.current === accountIdsKey) {
					setError(error);
					setIsLoading(false);
				}
			},
			complete: () => {
				if (!disposed.current && inFlightFor.current === accountIdsKey) {
					setIsLoading(false);
				}
			},
		});

		activeSubscription.current = subscription;

		return () => cancelSubscription(subscription, accountIdsKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		cancelPreviousRequest,
		cancelSubscription,
		environment,
		resetState,
		shouldHydrate,
		accountIdsKey,
	]);

	if (accountIds.length === 0 || !shouldHydrate) {
		return null;
	}

	return { users, isLoading, error };
}
